/**
 * Timesheet Cache Service
 * 
 * Provides caching functionality specifically for the VA timesheet system.
 * Implements intelligent caching strategies for contracts, timesheet entries,
 * and calculated summaries with proper invalidation patterns.
 * 
 * Features:
 * - Hash-based filter keys for complex query caching
 * - Surgical cache invalidation (invalidate only affected keys)
 * - TTL optimization based on data volatility
 * - RLS-aware caching (respects user permissions)
 * - Performance monitoring integration
 */

import redisClient from '../utils/redisClient.js';
import cacheMonitor from './cacheMonitoringService.js';

// Cache TTL defaults (in seconds) - optimized for timesheet data
const TTL = {
  CONTRACTS: 600,           // 10 minutes - rarely changes
  ENTRIES: 900,             // 15 minutes - frequently filtered, moderate volatility
  SUMMARY: 300,             // 5 minutes - calculated data, should refresh often
  CONTRACT_RATES: 1800,     // 30 minutes - static data
  ENTRY_SINGLE: 600,        // 10 minutes - individual entry details
};

// Cache key prefixes for timesheet data
const PREFIX = {
  CONTRACTS: 'timesheet:contracts',
  ENTRIES: 'timesheet:entries',
  SUMMARY: 'timesheet:summary',
  CONTRACT_RATES: 'timesheet:contract_rates',
  ENTRY: 'timesheet:entry',
};

class TimesheetCacheService {
  constructor() {
    this.redis = redisClient;
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0,
    };
  }

  /**
   * Generate a hash from filter parameters for cache key uniqueness
   * Uses btoa() for URL-safe base64 encoding
   */
  generateFilterHash(filters) {
    const {
      dateRange = {},
      statusFilter = 'all',
      contractFilter = 'all'
    } = filters;

    const filterString = `${dateRange.start || ''}:${dateRange.end || ''}:${statusFilter}:${contractFilter}`;
    
    try {
      // Use btoa for browser-safe base64 encoding
      return btoa(filterString);
    } catch (error) {
      console.error('[TimesheetCache] Failed to generate filter hash:', error);
      // Fallback to simple string hash if btoa fails
      return filterString.replace(/[^a-zA-Z0-9]/g, '_');
    }
  }

  /**
   * Build a cache key with proper namespacing
   */
  buildKey(prefix, identifier) {
    return `${prefix}:${identifier}`;
  }

  /**
   * Get and parse cached value with automatic JSON handling
   */
  async get(key, featureName = 'timesheet') {
    const startTime = Date.now();
    
    try {
      const value = await this.redis.get(key);
      const latency = Date.now() - startTime;
      
      if (value !== null) {
        this.stats.hits++;
        
        // Track cache hit
        await cacheMonitor.trackHit(featureName);
        await cacheMonitor.trackLatency(featureName, latency);
        
        // Auto-parse JSON
        if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
          try {
            return JSON.parse(value);
          } catch (e) {
            console.warn('[TimesheetCache] Failed to parse cached JSON:', e);
            return value;
          }
        }
        
        return value;
      }
      
      this.stats.misses++;
      await cacheMonitor.trackMiss(featureName);
      await cacheMonitor.trackLatency(featureName, latency);
      return null;
    } catch (error) {
      console.error(`[TimesheetCache] Failed to get ${key}:`, error);
      await cacheMonitor.trackError(featureName, error);
      this.stats.misses++;
      return null;
    }
  }

  /**
   * Set a value in cache with TTL and JSON serialization
   */
  async set(key, value, ttl, featureName = 'timesheet') {
    const startTime = Date.now();
    
    try {
      // Serialize objects/arrays to JSON
      const serialized = typeof value === 'object' ? JSON.stringify(value) : value;
      
      await this.redis.set(key, serialized, ttl);
      
      this.stats.sets++;
      await cacheMonitor.trackLatency(featureName, Date.now() - startTime);
      
      return true;
    } catch (error) {
      console.error(`[TimesheetCache] Failed to set ${key}:`, error);
      await cacheMonitor.trackError(featureName, error);
      return false;
    }
  }

  /**
   * Delete cache keys matching a pattern
   */
  async deletePattern(pattern, featureName = 'timesheet') {
    const startTime = Date.now();
    
    try {
      const deletedCount = await this.redis.delete(pattern);
      
      this.stats.deletes += deletedCount;
      await cacheMonitor.trackLatency(featureName, Date.now() - startTime);
      
      console.log(`[TimesheetCache] Deleted ${deletedCount} keys matching: ${pattern}`);
      return deletedCount;
    } catch (error) {
      console.error(`[TimesheetCache] Failed to delete pattern ${pattern}:`, error);
      await cacheMonitor.trackError(featureName, error);
      return 0;
    }
  }

  // ============================================================================
  // CONTRACTS CACHING
  // ============================================================================

  /**
   * Get cached contracts for a VA
   */
  async getContracts(userId) {
    const key = this.buildKey(PREFIX.CONTRACTS, userId);
    return await this.get(key, 'timesheet_contracts');
  }

  /**
   * Cache contracts for a VA
   */
  async setContracts(userId, contracts) {
    const key = this.buildKey(PREFIX.CONTRACTS, userId);
    return await this.set(key, contracts, TTL.CONTRACTS, 'timesheet_contracts');
  }

  /**
   * Invalidate contracts cache for a VA
   */
  async invalidateContracts(userId) {
    const key = this.buildKey(PREFIX.CONTRACTS, userId);
    return await this.redis.delete(key);
  }

  // ============================================================================
  // TIMESHEET ENTRIES CACHING
  // ============================================================================

  /**
   * Get cached timesheet entries with filters
   */
  async getEntries(userId, filters) {
    const filterHash = this.generateFilterHash(filters);
    const key = this.buildKey(PREFIX.ENTRIES, `${userId}:${filterHash}`);
    return await this.get(key, 'timesheet_entries');
  }

  /**
   * Cache timesheet entries with filters
   */
  async setEntries(userId, filters, entries) {
    const filterHash = this.generateFilterHash(filters);
    const key = this.buildKey(PREFIX.ENTRIES, `${userId}:${filterHash}`);
    return await this.set(key, entries, TTL.ENTRIES, 'timesheet_entries');
  }

  /**
   * Invalidate all timesheet entries for a user
   * Uses pattern matching to clear all filter combinations
   */
  async invalidateAllEntries(userId) {
    const pattern = `${PREFIX.ENTRIES}:${userId}:*`;
    return await this.deletePattern(pattern, 'timesheet_entries');
  }

  /**
   * Invalidate specific entry cache
   */
  async invalidateEntry(entryId) {
    const key = this.buildKey(PREFIX.ENTRY, entryId);
    return await this.redis.delete(key);
  }

  // ============================================================================
  // SUMMARY CACHING
  // ============================================================================

  /**
   * Get cached summary statistics
   */
  async getSummary(userId, filters) {
    const filterHash = this.generateFilterHash(filters);
    const key = this.buildKey(PREFIX.SUMMARY, `${userId}:${filterHash}`);
    return await this.get(key, 'timesheet_summary');
  }

  /**
   * Cache summary statistics
   */
  async setSummary(userId, filters, summary) {
    const filterHash = this.generateFilterHash(filters);
    const key = this.buildKey(PREFIX.SUMMARY, `${userId}:${filterHash}`);
    return await this.set(key, summary, TTL.SUMMARY, 'timesheet_summary');
  }

  /**
   * Invalidate all summaries for a user
   */
  async invalidateAllSummaries(userId) {
    const pattern = `${PREFIX.SUMMARY}:${userId}:*`;
    return await this.deletePattern(pattern, 'timesheet_summary');
  }

  // ============================================================================
  // CONTRACT RATES CACHING (for salary calculations)
  // ============================================================================

  /**
   * Get cached contract rate
   */
  async getContractRate(contractId) {
    const key = this.buildKey(PREFIX.CONTRACT_RATES, contractId);
    return await this.get(key, 'timesheet_rates');
  }

  /**
   * Cache contract rate
   */
  async setContractRate(contractId, rate) {
    const key = this.buildKey(PREFIX.CONTRACT_RATES, contractId);
    return await this.set(key, rate, TTL.CONTRACT_RATES, 'timesheet_rates');
  }

  // ============================================================================
  // COMPREHENSIVE INVALIDATION
  // ============================================================================

  /**
   * Invalidate all timesheet-related caches for a user
   * Called on: New entry creation, entry updates, real-time events
   */
  async invalidateAllTimesheetData(userId) {
    console.log(`[TimesheetCache] Invalidating all timesheet data for user: ${userId}`);
    
    const invalidationPromises = [
      this.invalidateContracts(userId),
      this.invalidateAllEntries(userId),
      this.invalidateAllSummaries(userId),
    ];

    try {
      await Promise.all(invalidationPromises);
      console.log(`[TimesheetCache] Successfully invalidated all timesheet data for user: ${userId}`);
      return true;
    } catch (error) {
      console.error(`[TimesheetCache] Error invalidating timesheet data:`, error);
      return false;
    }
  }

  /**
   * Surgical invalidation - invalidate only affected caches
   * More efficient than full invalidation for single entry updates
   */
  async invalidateTimesheetEntry(userId, entryId) {
    console.log(`[TimesheetCache] Surgical invalidation for user ${userId}, entry ${entryId}`);
    
    const invalidationPromises = [
      this.invalidateAllEntries(userId),      // Invalidate filtered queries
      this.invalidateAllSummaries(userId),    // Invalidate calculated summaries
      this.invalidateEntry(entryId),          // Invalidate specific entry
    ];

    try {
      await Promise.all(invalidationPromises);
      console.log(`[TimesheetCache] Successfully invalidated entry ${entryId}`);
      return true;
    } catch (error) {
      console.error(`[TimesheetCache] Error in surgical invalidation:`, error);
      return false;
    }
  }

  // ============================================================================
  // CACHE WARMING (On-Demand - Option C)
  // ============================================================================

  /**
   * Warm cache based on user interaction
   * Called when filters are applied or date range changes
   */
  async warmCache(userId, filters, dataFetcher) {
    console.log(`[TimesheetCache] Warming cache for user: ${userId}`);
    
    try {
      // Check if already cached
      const cached = await this.getEntries(userId, filters);
      if (cached) {
        console.log(`[TimesheetCache] Cache already warm for filters`);
        return cached;
      }

      // Fetch fresh data
      const data = await dataFetcher();
      
      // Cache the data
      await this.setEntries(userId, filters, data);
      
      console.log(`[TimesheetCache] Successfully warmed cache with ${data?.length || 0} entries`);
      return data;
    } catch (error) {
      console.error(`[TimesheetCache] Error warming cache:`, error);
      return null;
    }
  }

  // ============================================================================
  // STATISTICS & MONITORING
  // ============================================================================

  /**
   * Get cache statistics
   */
  getStats() {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? ((this.stats.hits / total) * 100).toFixed(2) : '0.00';
    
    return {
      ...this.stats,
      hitRate: `${hitRate}%`,
    };
  }

  /**
   * Reset cache statistics
   */
  resetStats() {
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0,
    };
  }
}

// Export singleton instance
const timesheetCacheService = new TimesheetCacheService();
export default timesheetCacheService;
